import React, { useState } from 'react';
import Header from '../components/Header';
import { ChevronDown, ChevronUp, Search } from 'lucide-react';
import { Input } from '../components/ui/input';

const FAQ_SECTIONS = [
  {
    title: 'Booking',
    items: [
      {
        q: 'How do I book a service?',
        a: 'Browse the Discover page, select a service, choose your preferred date and time, and proceed to payment. You can book as a guest or create an account.',
      },
      {
        q: 'Do I need an account to book?',
        a: 'No, we offer guest checkout. Just provide your name, email, and phone number. However, creating an account lets you track bookings, leave reviews, and rebook easily.',
      },
      {
        q: 'Can I book a mobile stylist?',
        a: 'Yes! Enter your postcode on the Discover page and we\'ll show mobile stylists who cover your area, sorted by distance.',
      },
      {
        q: 'How do I cancel or reschedule?',
        a: 'Contact us at support@zuriya.co.uk with your booking reference. Cancellations more than 24 hours before the appointment receive a full refund. See our Refund & Cancellation Policy for full details.',
      },
    ],
  },
  {
    title: 'Payments',
    items: [
      {
        q: 'How do payments work?',
        a: 'Payments are processed securely through Stripe at the time of booking. We accept all major debit and credit cards.',
      },
      {
        q: 'Is there a booking fee?',
        a: 'No. Clients never pay a booking fee on Zuriya. The price shown on the service is the price you pay, and any deposit is part of that price, not an extra charge.',
      },
      {
        q: 'When do I get charged?',
        a: 'When you confirm your booking you pay either the full price (including any add-ons) or the deposit the provider asks for. The amount due now and the balance due at the appointment are both shown before you pay.',
      },
      {
        q: 'How long do refunds take?',
        a: 'Refunds go back to your original payment method and usually appear within 5 to 10 business days. Zuriya never deducts its own fee from a refund.',
      },
    ],
  },
  {
    title: 'For Providers',
    items: [
      {
        q: 'How do I join as a provider?',
        a: 'Click "Join as Stylist" and complete the onboarding form with your business details, services, and location. Your profile will be reviewed and approved by our team.',
      },
      {
        q: 'What are the fees?',
        a: 'During the Founding Provider Beta, clients pay no booking fee. A single fee (currently 10%) comes out of your payout on new marketplace clients and already covers card processing, so a £100 booking pays you £90. If you take a deposit, the fee is still worked out on the full booking price and comes out of the deposit, and the balance the client pays you in person is all yours. If a booking is refunded, the fee on the refunded amount is refunded too. Repeat clients and bookings through your own link carry no platform fee, with standard processing (1.75% + 20p) still applying. After beta the platform commission is 15% on the Free plan (8% on Pro) for new clients, and 0% on repeat clients, plus processing of 1.75% + 20p (Free) or 1.55% + 20p (Pro).',
      },
      {
        q: 'How do payouts work?',
        a: 'Payouts are processed weekly once your available balance reaches £100. If your balance is below £100, it rolls over to the next cycle. You can track your earnings in the Earnings tab of your dashboard.',
      },
      {
        q: 'Can I set my own prices?',
        a: 'Yes, you have full control over your service pricing, add-ons, and service descriptions.',
      },
    ],
  },
  {
    title: 'Account & Privacy',
    items: [
      {
        q: 'How do I delete my account?',
        a: 'Contact privacy@zuriya.co.uk and we will process your account deletion request in line with UK GDPR requirements.',
      },
      {
        q: 'Is my data safe?',
        a: 'Yes. We use encrypted passwords, HTTPS, and Stripe for secure payment processing. We never sell your personal data. See our Privacy Policy for full details.',
      },
      {
        q: 'Can I change my email address?',
        a: 'Contact support@zuriya.co.uk and we\'ll help you update your account details.',
      },
    ],
  },
];

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#E6E4DD] last:border-b-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-4 text-left"
        data-testid={`faq-q-${q.slice(0, 20).replace(/\s+/g, '-').toLowerCase()}`}
      >
        <span className="text-sm font-medium text-[#1A1A1A] pr-4">{q}</span>
        {open ? (
          <ChevronUp className="h-4 w-4 text-stone-400 flex-shrink-0" />
        ) : (
          <ChevronDown className="h-4 w-4 text-stone-400 flex-shrink-0" />
        )}
      </button>
      {open && (
        <div className="pb-4 pr-8">
          <p className="text-sm text-stone-500 leading-relaxed">{a}</p>
        </div>
      )}
    </div>
  );
}

export default function HelpCentre() {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredSections = FAQ_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter(
      (item) =>
        !searchQuery ||
        item.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.a.toLowerCase().includes(searchQuery.toLowerCase())
    ),
  })).filter((section) => section.items.length > 0);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-12">
        <div className="text-center mb-10">
          <h1 className="font-heading text-3xl sm:text-4xl font-medium text-[#1A1A1A] mb-3" data-testid="help-title">Help Centre</h1>
          <p className="text-stone-500 mb-6">Find answers to common questions about booking, payments, and using Zuriya.</p>
          <div className="relative max-w-md mx-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search for help..."
              className="input-zuriya pl-10"
              data-testid="help-search-input"
            />
          </div>
        </div>

        {filteredSections.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-stone-500 mb-2">No results found for "{searchQuery}"</p>
            <p className="text-sm text-stone-400">Try a different search or contact <a href="mailto:support@zuriya.co.uk" className="text-[#D4AF37] underline">support@zuriya.co.uk</a></p>
          </div>
        ) : (
          <div className="space-y-8" data-testid="faq-sections">
            {filteredSections.map((section) => (
              <div key={section.title} className="card-surface p-6">
                <h2 className="font-heading text-lg font-medium text-[#1A1A1A] mb-4">{section.title}</h2>
                <div>
                  {section.items.map((item) => (
                    <FaqItem key={item.q} q={item.q} a={item.a} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-12 text-center card-surface p-8">
          <h3 className="font-heading text-lg font-medium text-[#1A1A1A] mb-2">Still need help?</h3>
          <p className="text-sm text-stone-500 mb-4">Our team is here to help. Reach out and we'll get back to you as soon as possible.</p>
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <a href="mailto:support@zuriya.co.uk" className="px-4 py-2 bg-[#D4AF37] text-white rounded-full font-medium hover:opacity-90 transition-opacity" data-testid="help-email-btn">
              support@zuriya.co.uk
            </a>
            <a href="mailto:providers@zuriya.co.uk" className="px-4 py-2 border border-[#D4AF37] text-[#D4AF37] rounded-full font-medium hover:bg-[#D4AF37] hover:text-white transition-colors" data-testid="help-providers-btn">
              Provider Support
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
